
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Table, Filter, FilterValues, PageHeader, Card, FilterTypes, QuickFilterTypes } from "@cofano/cck";
import { Box } from "@mui/material";

import { TableId, useBackendTable } from "components/table";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import Helmet from "components/helmet/Helmet";

import { Language } from "imports/Utils";

import { RelationLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";
import useOpenCharterSideviewer from "view/charters/components/sideviewer/useOpenCharterSideviewer";

import { ChildPropsFromPagination } from "types/commons";
import { CharterSubtype, CharterType, SortDirection } from "__generated__/enums";
import { GetSpotSalesChartersDocument, GetSpotSalesChartersQuery } from "__generated__/view/charters/graphql/charter.queries.generated";

type SpotSalesCharterFromQuery = ChildPropsFromPagination<GetSpotSalesChartersQuery, "salesCharters">;

const SpotSalesCharters = () => {

	const client = useApolloClient();
	const navigate = useNavigate();

	const { tableProps, filterProps, filterValuesProps, refetch } = useBackendTable({
		id: TableId.SpotSalesCharters,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetSpotSalesChartersDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort ?? [ {
								field: "createdOn",
								direction: SortDirection.Desc,
							} ],
						},
						search: {
							charterNumber: filter.charterNumber,
							customerReference: filter.customerReference,
							charterType: CharterType.Sales,
							charterSubtypes: [ CharterSubtype.Spot ],
							creditors: filter.creditor?.map((option: RelationSelectOption) => option.id),
							debtors: filter.debtor?.map((option: RelationSelectOption) => option.id),
						},
					},
				});

				callback({
					content: result.data.salesCharters.content ?? [],
					totalResults: result.data.salesCharters.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const openCharterSideviewer = useOpenCharterSideviewer();


	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("SPOT_SALES_CHARTERS")}
					<Helmet title={intl.formatMessage({ id: "SPOT_SALES_CHARTERS" })} />
				</PageHeader.Title>
			</PageHeader>

			{filterValuesProps.values.length > 0 && (
				<Box mb={2}>
					<Card disablePadding>
						<FilterValues
							{...filterValuesProps}
							fields={[ {
								id: "charterNumber",
								title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
							}, {
								id: "creditor",
								title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
								renderFilterValue: (value) => value.map((option: RelationSelectOption) => option.name).join(", "),
							}, {
								id: "debtor",
								title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
								renderFilterValue: (value) => value.map((option: RelationSelectOption) => option.name).join(", "),
							} ]} />
					</Card>
				</Box>
			)}

			<Card disablePadding>
				<Table
					{...tableProps}
					onRowClick={row => {
						if (row.id) {
							openCharterSideviewer(row.id, () => refetch());
						}
					}}
					onRowDoubleClick={row => navigate(`/view/spotsalescharters/${row.id}/details`)}
					filterComponent={() => (
						<Filter
							{...filterProps}
							fields={[ {
								id: "charterNumber",
								title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
								filterType: FilterTypes.TEXT,
							}, {
								id: "creditor",
								title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
								render: (value, onChange) => (
									<RelationSelect
										value={value}
										onChange={options => onChange(options)}
										multiple
									/>
								),
							}, {
								id: "debtor",
								title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
								render: (value, onChange) => (
									<RelationSelect
										value={value}
										onChange={options => onChange(options)}
										multiple
									/>
								),
							} ]} />
					)}
					columns={[ {
						id: "charterNumber",
						title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
						sortable: true,
						filterComponent: QuickFilterTypes.TEXT,
						width: 150,
					}, {
						id: "creditor",
						title: intl.formatMessage({ id: "INVOICE_CREDITOR" }),
						sortable: true,
						sortKey: "creditor.name",
						width: 200,
						render: (row: SpotSalesCharterFromQuery) => <RelationLink relation={row.creditor} />,
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "INVOICE_CREDITOR" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
					}, {
						id: "debtor",
						title: intl.formatMessage({ id: "INVOICE_DEBTOR" }),
						sortable: true,
						sortKey: "debtor.name",
						width: 200,
						render: (row: SpotSalesCharterFromQuery) => <RelationLink relation={row.debtor} />,
						filterComponent: (value, onChange) => (
							<RelationSelect
								label={intl.formatMessage({ id: "INVOICE_DEBTOR" })}
								value={value}
								onChange={options => onChange(options)}
								multiple />
						),
					}, {
						id: "customerReference",
						title: intl.formatMessage({ id: "CUSTOMER_REFERENCE" }),
						width: 200,
						filterComponent: QuickFilterTypes.TEXT,
					}, {
						id: "nrCargos",
						title: intl.formatMessage({ id: "NUMBERS_OF_CONTAINERS" }),
						render: (row: SpotSalesCharterFromQuery) => row.cargos.length,
						width: 100,
						disableFlexGrow: true,
					}, {
						id: "createdOn",
						title: intl.formatMessage({ id: "CREATED" }),
						sortable: true,
						width: 150,
						render: (row: SpotSalesCharterFromQuery) => <FormattedDate date={row.createdOn} withoutYear />,
					} ]} />
			</Card>
		</div>
	);
};

export default SpotSalesCharters;
